"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { api, ApiError, TOKEN_REFRESHED_EVENT } from "./api";

const TOKEN_KEY = "yuzhou.accessToken";
const REFRESH_KEY = "yuzhou.refreshToken";
const USER_KEY = "yuzhou.user";

export type Role = "teacher" | "student" | "ta";

export interface AuthUser {
  userId: string;
  schoolId: string;
  displayName: string;
  role: Role;
  schoolName?: string;
  className?: string;
}

interface LoginResponse {
  accessToken: string;
  refreshToken: string;
  user: AuthUser;
}

interface AuthContextValue {
  user: AuthUser | null;
  token: string | null;
  loading: boolean;
  loginTeacher: (email: string, password: string) => Promise<AuthUser>;
  loginStudent: (username: string, password: string) => Promise<AuthUser>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const t = localStorage.getItem(TOKEN_KEY);
    const u = localStorage.getItem(USER_KEY);
    if (t && u) {
      try {
        setToken(t);
        setUser(JSON.parse(u) as AuthUser);
      } catch {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
      }
    }
    setLoading(false);

    const onStorage = (e: StorageEvent) => {
      if (e.key === TOKEN_KEY && !e.newValue) {
        setToken(null);
        setUser(null);
      }
    };
    const onRefreshed = (e: Event) => {
      const ev = e as CustomEvent<{ accessToken: string; user: AuthUser }>;
      setToken(ev.detail.accessToken);
      setUser(ev.detail.user);
    };
    window.addEventListener("storage", onStorage);
    window.addEventListener(TOKEN_REFRESHED_EVENT, onRefreshed);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener(TOKEN_REFRESHED_EVENT, onRefreshed);
    };
  }, []);

  const persist = useCallback((res: LoginResponse) => {
    localStorage.setItem(TOKEN_KEY, res.accessToken);
    localStorage.setItem(REFRESH_KEY, res.refreshToken);
    localStorage.setItem(USER_KEY, JSON.stringify(res.user));
    setToken(res.accessToken);
    setUser(res.user);
  }, []);

  const loginTeacher = useCallback(
    async (email: string, password: string) => {
      const res = await api.post<LoginResponse>("/api/auth/teacher/login", {
        email,
        password,
      });
      persist(res);
      return res.user;
    },
    [persist],
  );

  const loginStudent = useCallback(
    async (username: string, password: string) => {
      const res = await api.post<LoginResponse>("/api/auth/student/login", {
        username,
        password,
      });
      persist(res);
      return res.user;
    },
    [persist],
  );

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_KEY);
    localStorage.removeItem(USER_KEY);
    setToken(null);
    setUser(null);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ user, token, loading, loginTeacher, loginStudent, logout }),
    [user, token, loading, loginTeacher, loginStudent, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}

export function getStoredToken(): string | null {
  if (typeof window === "undefined") return null;
  return localStorage.getItem(TOKEN_KEY);
}

export { ApiError };
